/* DentPDR collection and search: <collection-listing>, <desc-toggle>, <search-tabs>. Spec: specs/collection-search. */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const debounce = (fn, ms) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  class CollectionListing extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-dp-form]');
      this.filters = this.querySelector('[data-dp-filters]');
      this.dim = this.querySelector('[data-dp-dim]');
      this.openBtn = this.querySelector('[data-dp-open-filters]');
      this.controller = null;
      this.release = null;
      this.isOpen = false;
      this.applyDebounced = debounce(() => this.apply(), 500);

      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('input', (e) => this.onInput(e));
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('submit', (e) => {
        if (e.target === this.form) {
          e.preventDefault();
          this.apply();
        }
      });
      this.onKeydown = (e) => {
        if (e.key === 'Escape' && this.isOpen) this.closeSheet();
      };
      this.onPopstate = () => this.load(location.href, { push: false });
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopstate);

      this.initFacets();
      this.initSliders();
      this.initDensity();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('popstate', this.onPopstate);
      if (this.isOpen) this.closeSheet();
    }

    /* events */
    onChange(e) {
      const t = e.target;
      if (t.matches('[data-dp-facet-input]') || t.matches('[data-dp-sort]')) {
        this.apply();
      } else if (t.matches('[data-dp-price-input]')) {
        this.syncSlider(t.closest('[data-dp-price]'));
        this.apply();
      }
    }

    onInput(e) {
      const t = e.target;
      if (t.matches('[data-dp-price-input]')) {
        this.syncSlider(t.closest('[data-dp-price]'));
        this.applyDebounced();
      }
    }

    onClick(e) {
      const t = e.target;
      const link = t.closest('[data-dp-link]');
      if (link && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button)) {
        e.preventDefault();
        const isChip = link.matches('[data-dp-chip], [data-dp-chip-clear], [data-dp-clear-all]');
        this.load(link.href, { push: true, focusChips: isChip, scrollTop: link.matches('[data-dp-page]') });
        return;
      }
      if (t.closest('[data-dp-open-filters]')) return this.openSheet();
      if (t.closest('[data-dp-close-filters]') || t === this.dim) return this.closeSheet();
      const more = t.closest('[data-dp-load-more]');
      if (more) return this.loadMore(more);
      const toggle = t.closest('[data-dp-facet-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', String(toggle.getAttribute('aria-expanded') !== 'true'));
        return;
      }
      const facetMore = t.closest('[data-dp-facet-more]');
      if (facetMore) return this.toggleMore(facetMore);
      const density = t.closest('[data-dp-density] [data-density]');
      if (density) this.setDensity(density.dataset.density);
    }

    /* request and region sync */
    buildUrl() {
      const url = new URL(this.form.getAttribute('action') || location.pathname, location.href);
      for (const [k, v] of new FormData(this.form)) {
        if (v !== '' && k !== 'page') url.searchParams.append(k, v);
      }
      return url;
    }

    apply() {
      this.load(this.buildUrl().href, { push: true });
    }

    async fetchSection(href, signal) {
      const url = new URL(href, location.href);
      url.searchParams.set('section_id', this.sectionId);
      const res = await fetch(url, { signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error(String(res.status));
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = doc.querySelector('collection-listing');
      if (!next) throw new Error('missing');
      return next;
    }

    setBusy(on) {
      const count = this.querySelector('[data-dp-count]');
      if (on) {
        this.setAttribute('aria-busy', 'true');
        if (count) count.dataset.loading = '';
      } else {
        this.removeAttribute('aria-busy');
        if (count) delete count.dataset.loading;
      }
    }

    async load(href, { push = true, focusChips = false, scrollTop = false } = {}) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.setBusy(true);
      const results = this.querySelector('[data-dp-results]');
      const skeleton = this.querySelector('template[data-dp-skeleton]');
      if (results && skeleton) results.replaceChildren(skeleton.content.cloneNode(true));
      try {
        const next = await this.fetchSection(href, signal);
        this.syncRegions(next);
        if (push) history.pushState({ dp: true }, '', href);
        this.setBusy(false);
        if (focusChips) this.focusAfterChip();
        if (scrollTop) this.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      } catch (err) {
        if (err.name === 'AbortError') return;
        location.href = href;
      }
    }

    async loadMore(btn) {
      const href = btn.dataset.next;
      if (!href) return;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      try {
        const next = await this.fetchSection(href, this.controller.signal);
        const grid = this.querySelector('[data-dp-grid]');
        const incoming = [...next.querySelectorAll('[data-dp-grid] > .dp-pgrid__item')];
        incoming.forEach((item) => grid.appendChild(document.importNode(item, true)));
        this.querySelector('[data-dp-pager]').innerHTML = next.querySelector('[data-dp-pager]').innerHTML;
        history.replaceState({ dp: true }, '', href);
        const firstLink = grid.children[grid.children.length - incoming.length]?.querySelector('a');
        if (firstLink) firstLink.focus();
      } catch (err) {
        if (err.name === 'AbortError') return;
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
      }
    }

    syncRegions(next) {
      const swap = (sel) => {
        const cur = this.querySelector(sel);
        const nxt = next.querySelector(sel);
        if (cur && nxt) cur.innerHTML = nxt.innerHTML;
        return [cur, nxt];
      };
      const [count, nextCount] = [this.querySelector('[data-dp-count]'), next.querySelector('[data-dp-count]')];
      if (count && nextCount) count.textContent = nextCount.textContent;
      const [chips, nextChips] = swap('[data-dp-chips]');
      if (chips && nextChips) chips.hidden = nextChips.hidden;
      const [results, nextResults] = swap('[data-dp-results]');
      if (results && nextResults) results.dataset.total = nextResults.dataset.total;
      swap('[data-dp-pager]');
      const badge = this.querySelector('[data-dp-filter-badge]');
      const nextBadge = next.querySelector('[data-dp-filter-badge]');
      if (badge && nextBadge) {
        badge.textContent = nextBadge.textContent;
        badge.hidden = nextBadge.hidden;
      }
      const show = this.querySelector('[data-dp-show-results]');
      const nextShow = next.querySelector('[data-dp-show-results]');
      if (show && nextShow) show.textContent = nextShow.textContent;
      this.syncFacets(next);
    }

    syncFacets(next) {
      next.querySelectorAll('.dp-facet__body').forEach((body) => {
        const cur = this.querySelector(`#${CSS.escape(body.id)}`);
        if (!cur) return;
        const group = cur.closest('[data-dp-facet-group]');
        const nextGroup = body.closest('[data-dp-facet-group]');
        group.dataset.active = nextGroup.dataset.active;
        const badge = group.querySelector('[data-dp-facet-badge]');
        const nextBadge = nextGroup.querySelector('[data-dp-facet-badge]');
        badge.textContent = nextBadge.textContent;
        badge.hidden = nextBadge.hidden;
        body.querySelectorAll('[data-dp-facet-input]').forEach((input) => {
          const mine = cur.querySelector(`#${CSS.escape(input.id)}`);
          if (!mine) return;
          mine.checked = input.checked;
          mine.disabled = input.disabled;
          mine.closest('.dp-facet__row').classList.toggle('is-disabled', input.disabled);
          mine.closest('.dp-facet__row').querySelector('[data-dp-facet-count]').textContent = input.closest('.dp-facet__row').querySelector('[data-dp-facet-count]').textContent;
        });
        body.querySelectorAll('[data-dp-price-input]').forEach((input) => {
          const mine = cur.querySelector(`[data-dp-price-input="${input.dataset.dpPriceInput}"]`);
          if (mine && mine !== document.activeElement) mine.value = input.value;
        });
        const price = cur.querySelector('[data-dp-price]');
        if (price) this.syncSlider(price);
      });
    }

    focusAfterChip() {
      const chip = this.querySelector('[data-dp-chip]');
      const target = chip || (this.openBtn && this.openBtn.offsetParent ? this.openBtn : this.querySelector('[data-dp-sort]'));
      if (target) target.focus();
    }

    /* filter drawer (all widths) */
    openSheet() {
      if (!this.filters || this.isOpen) return;
      this.isOpen = true;
      this.filters.setAttribute('role', 'dialog');
      this.filters.setAttribute('aria-modal', 'true');
      this.dim.hidden = false;
      requestAnimationFrame(() => {
        if (!this.isOpen) return;
        this.filters.classList.add('is-open');
        this.dim.classList.add('is-open');
        // focus only once is-open has made the drawer visible
        this.filters.querySelector('[data-dp-close-filters]')?.focus();
      });
      this.openBtn?.setAttribute('aria-expanded', 'true');
      if (window.DP?.lockScroll) window.DP.lockScroll();
      if (window.DP?.trapFocus) this.release = window.DP.trapFocus(this.filters, this.openBtn);
      this.dispatchEvent(new CustomEvent('dp:overlay:change', { bubbles: true, detail: { open: true } }));
    }

    closeSheet() {
      this.isOpen = false;
      this.filters.classList.remove('is-open');
      this.dim.classList.remove('is-open');
      this.filters.setAttribute('role', 'region');
      this.filters.removeAttribute('aria-modal');
      this.openBtn?.setAttribute('aria-expanded', 'false');
      setTimeout(() => {
        if (!this.isOpen) this.dim.hidden = true;
      }, reducedMotion() ? 0 : 250);
      if (this.release) this.release();
      this.release = null;
      if (window.DP?.unlockScroll) window.DP.unlockScroll();
      if (this.openBtn && this.openBtn.offsetParent) this.openBtn.focus();
      this.dispatchEvent(new CustomEvent('dp:overlay:change', { bubbles: true, detail: { open: false } }));
    }

    /* facet groups */
    initFacets() {
      this.querySelectorAll('[data-dp-facet-more]').forEach((btn) => {
        btn.hidden = false;
        const group = btn.closest('.dp-facet');
        if (group.querySelector('.dp-facet__item--extra input:checked')) this.toggleMore(btn);
      });
      this.querySelectorAll('[data-dp-facet-group]').forEach((group) => {
        if (group.dataset.active === '0') group.querySelector('[data-dp-facet-toggle]').setAttribute('aria-expanded', 'false');
      });
    }

    toggleMore(btn) {
      const group = btn.closest('.dp-facet');
      const open = !group.classList.contains('is-expanded');
      group.classList.toggle('is-expanded', open);
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
    }

    /* density */
    initDensity() {
      const group = this.querySelector('[data-dp-density]');
      if (!group) return;
      group.hidden = false;
      let saved = '4';
      try {
        saved = localStorage.getItem('dp:grid-density') || '4';
      } catch (e) {}
      this.setDensity(saved === '3' ? '3' : '4', false);
    }

    setDensity(value, persist = true) {
      this.dataset.density = value;
      this.querySelectorAll('[data-dp-density] [data-density]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.density === value)));
      if (!persist) return;
      try {
        localStorage.setItem('dp:grid-density', value);
      } catch (e) {}
    }

    /* price slider (progressive enhancement over the two number inputs) */
    initSliders() {
      this.querySelectorAll('[data-dp-price]').forEach((price) => {
        const slider = price.querySelector('[data-dp-slider]');
        if (!slider) return;
        slider.hidden = false;
        this.syncSlider(price);
        slider.querySelectorAll('[data-dp-handle]').forEach((handle) => {
          handle.addEventListener('pointerdown', (e) => {
            e.preventDefault();
            handle.setPointerCapture(e.pointerId);
            const rect = slider.getBoundingClientRect();
            const move = (ev) => {
              const pct = clamp((ev.clientX - rect.left) / rect.width, 0, 1);
              this.setHandle(price, handle.dataset.dpHandle, Math.round(this.bounds(price).min + pct * (this.bounds(price).max - this.bounds(price).min)));
            };
            const up = () => {
              handle.removeEventListener('pointermove', move);
              handle.removeEventListener('pointerup', up);
              handle.removeEventListener('pointercancel', up);
              this.apply();
            };
            handle.addEventListener('pointermove', move);
            handle.addEventListener('pointerup', up);
            handle.addEventListener('pointercancel', up);
          });
          handle.addEventListener('keydown', (e) => {
            const { min, max } = this.bounds(price);
            const step = Math.max(1, Math.round((max - min) * 0.05));
            const cur = Number(handle.getAttribute('aria-valuenow'));
            const keys = { ArrowRight: cur + step, ArrowUp: cur + step, ArrowLeft: cur - step, ArrowDown: cur - step, Home: min, End: max };
            if (!(e.key in keys)) return;
            e.preventDefault();
            this.setHandle(price, handle.dataset.dpHandle, keys[e.key]);
            this.applyDebounced();
          });
        });
      });
    }

    bounds(price) {
      return { min: Number(price.dataset.min), max: Number(price.dataset.max) };
    }

    values(price) {
      const { min, max } = this.bounds(price);
      const lo = price.querySelector('[data-dp-price-input="min"]').value;
      const hi = price.querySelector('[data-dp-price-input="max"]').value;
      const a = lo === '' ? min : clamp(Number(lo), min, max);
      const b = hi === '' ? max : clamp(Number(hi), min, max);
      return { lo: Math.min(a, b), hi: Math.max(a, b) };
    }

    setHandle(price, which, value) {
      const { min, max } = this.bounds(price);
      const { lo, hi } = this.values(price);
      const input = price.querySelector(`[data-dp-price-input="${which}"]`);
      const v = which === 'min' ? clamp(value, min, hi) : clamp(value, lo, max);
      const isBound = which === 'min' ? v <= min : v >= max;
      input.value = isBound ? '' : String(v);
      this.syncSlider(price);
    }

    syncSlider(price) {
      const slider = price.querySelector('[data-dp-slider]');
      if (!slider || slider.hidden) return;
      const { min, max } = this.bounds(price);
      const { lo, hi } = this.values(price);
      const span = Math.max(1, max - min);
      const pLo = ((lo - min) / span) * 100;
      const pHi = ((hi - min) / span) * 100;
      const range = slider.querySelector('[data-dp-slider-range]');
      range.style.insetInlineStart = `${pLo}%`;
      range.style.width = `${pHi - pLo}%`;
      const hMin = slider.querySelector('[data-dp-handle="min"]');
      const hMax = slider.querySelector('[data-dp-handle="max"]');
      hMin.style.insetInlineStart = `${pLo}%`;
      hMax.style.insetInlineStart = `${pHi}%`;
      hMin.setAttribute('aria-valuenow', String(lo));
      hMax.setAttribute('aria-valuenow', String(hi));
    }
  }

  class DescToggle extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-desc-body]');
      this.btn = this.querySelector('button');
      if (!this.body || !this.btn) return;
      this.btn.addEventListener('click', () => {
        const open = !this.hasAttribute('data-open');
        this.toggleAttribute('data-open', open);
        this.btn.setAttribute('aria-expanded', String(open));
        this.btn.textContent = open ? this.btn.dataset.labelLess : this.btn.dataset.labelMore;
      });
      this.measure = () => {
        if (this.hasAttribute('data-open')) return;
        this.btn.hidden = this.body.scrollHeight <= this.body.clientHeight + 1;
      };
      this.measure();
      window.addEventListener('resize', this.measure);
    }

    disconnectedCallback() {
      if (this.measure) window.removeEventListener('resize', this.measure);
    }
  }

  // Search page: products are server-rendered; matching guides load into their own block above them, and an empty
  // product result gets a "did you mean" suggestion from predictive search.
  class SearchTabs extends HTMLElement {
    connectedCallback() {
      this.terms = this.dataset.terms;
      this.guides();
      if (this.querySelector('[data-dp-suggest]')) this.suggest();
    }

    async guides() {
      const box = this.querySelector('[data-dp-guides]');
      if (!box) return;
      const url = new URL(this.dataset.searchUrl, location.origin);
      url.searchParams.set('q', this.terms);
      url.searchParams.set('type', 'article');
      url.searchParams.set('section_id', 'search-guides');
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const src = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('[data-dp-guides-render]');
        const rows = src ? [...src.querySelectorAll('li')] : [];
        if (!rows.length) return;
        box.querySelector('[data-dp-guides-title]').textContent = box.dataset.label.replace('{n}', src.dataset.count);
        box.querySelector('[data-dp-guides-list]').replaceChildren(...rows);
        box.hidden = false;
      } catch (e) {}
    }

    async suggest() {
      const el = this.querySelector('[data-dp-suggest]');
      const url = new URL(this.dataset.endpoint, location.origin);
      url.searchParams.set('q', this.terms);
      url.searchParams.set('resources[type]', 'query');
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const queries = (await res.json()).resources.results.queries || [];
        const text = queries.length && queries[0].text;
        if (!text || text.toLowerCase() === this.terms.toLowerCase()) return;
        const href = `${location.pathname}?q=${encodeURIComponent(text)}&type=product`;
        const link = `<a class="dp-link" href="${href}"><strong>${escapeHtml(text)}</strong></a>`;
        el.innerHTML = el.dataset.template.replace('{s}', link);
        el.hidden = false;
      } catch (e) {}
    }
  }

  [['collection-listing', CollectionListing], ['desc-toggle', DescToggle], ['search-tabs', SearchTabs]].forEach(([name, cls]) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  });
})();
